import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  Calendar, MapPin, User, Phone, ArrowRight, ArrowLeft, FileText,
  Plane, Briefcase, Heart, AlertCircle, Check, Globe, Building, Shield
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ConferenceRegistration = () => {
  const { language } = useLanguage();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEditMode = searchParams.get('edit') === '1';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1);
  const [adminNote, setAdminNote] = useState('');
  const [eventsData, setEventsData] = useState(null); // {events, current}
  const totalSteps = 5;

  const [formData, setFormData] = useState({
    date_of_birth: '',
    gender: '',
    nationality: '',
    passport_number: '',
    passport_expiry: '',
    place_of_birth: '',
    address: '',
    city: '',
    state_province: '',
    postal_code: '',
    country: '',
    occupation: '',
    organization: '',
    organization_address: '',
    arrival_date: '',
    departure_date: '',
    needs_visa_letter: false,
    visa_embassy_city: '',
    visa_country: '',
    reason: '',
    dietary_requirements: '',
    heard_about: '',
    special_needs: '',
    emergency_contact: '',
    emergency_phone: '',
    emergency_relationship: '',
    agreeTerms: false
  });

  const t = {
    en: {
      title: "Conference Registration",
      subtitle: "Complete all sections to register for the Ottawa International Conference 2027",
      conferenceTitle: "Ottawa International Conference 2027",
      conferenceSubject: "HOW TO RECOVER AFTER: ABUSE, RAPE, HARASSMENT, MISTREATMENT, AND TRAUMA",
      date: "March 12-14, 2027",
      location: "Sheraton Hotel, 150 Albert St, Ottawa, Canada",
      fee: "Registration Fee: $499 USD",
      feeNote: "Payment will be requested after your registration is approved by the organizers.",

      step1: "Personal Information",
      step1desc: "As it appears on your passport / official ID",
      step2: "Address & Professional",
      step2desc: "Your residential address and professional details",
      step3: "Travel & Visa",
      step3desc: "Travel arrangements and visa invitation letter request",
      step4: "Conference Details",
      step4desc: "Your motivation and any special requirements",
      step5: "Emergency Contact",
      step5desc: "In case of emergency during the conference",

      dateOfBirth: "Date of Birth",
      gender: "Gender",
      genderOptions: { male: "Male", female: "Female", other: "Other", prefer_not: "Prefer not to say" },
      nationality: "Nationality",
      passportNumber: "Passport Number",
      passportExpiry: "Passport Expiry Date",
      placeOfBirth: "Place of Birth (City, Country)",
      passportNote: "Required for visa invitation letter. Your data is kept strictly confidential.",

      address: "Street Address",
      city: "City",
      stateProvince: "State / Province",
      postalCode: "Postal / ZIP Code",
      country: "Country of Residence",
      occupation: "Job Title / Occupation",
      organization: "Organization / Company",
      organizationAddress: "Organization Address",

      arrivalDate: "Planned Arrival Date",
      departureDate: "Planned Departure Date",
      needsVisaLetter: "I need a visa invitation letter for my travel to Canada",
      visaEmbassyCity: "City of Canadian Embassy/Consulate",
      visaCountry: "Country of Visa Application",
      visaNote: "If you need a visa invitation letter, please provide your passport details in Step 1 and the embassy information below. The letter will be sent to you after approval.",

      reason: "Why do you want to attend this conference?",
      reasonPlaceholder: "Share your motivation, what you hope to gain, and how this conference relates to your work or personal journey...",
      dietaryRequirements: "Dietary Requirements",
      dietaryPlaceholder: "e.g. Vegetarian, Halal, Gluten-free, Allergies...",
      heardAbout: "How did you hear about us?",
      heardOptions: { social: "Social Media", friend: "Friend / Colleague", church: "Church / Community", website: "Website", email: "Email", other: "Other" },
      specialNeeds: "Special Needs or Accommodations",
      specialPlaceholder: "Wheelchair access, hearing assistance, childcare needs, etc.",

      emergencyContact: "Full Name",
      emergencyPhone: "Phone Number (with country code)",
      emergencyRelationship: "Relationship",
      relationshipOptions: { spouse: "Spouse", parent: "Parent", sibling: "Sibling", friend: "Friend", colleague: "Colleague", other: "Other" },

      terms: "I certify that the information provided is accurate. I agree to the conference terms and conditions and understand that my registration will be reviewed before approval.",
      submit: "Submit Registration",
      next: "Continue",
      previous: "Back",
      backDashboard: "Back to Dashboard",
      success: "Registration submitted successfully! Your request is pending review.",
      loginRequired: "Please login to register for the conference",
      login: "Sign In",
      loading: "Submitting...",
      required: "Required",
      optional: "Optional",
      stepOf: "of",
      eventClosedTitle: "Registration Closed — Event Ended",
      eventClosedBody: "This conference has already concluded. Thank you for your interest! You cannot register for a past event.",
      currentEventsTitle: "Current & Upcoming Events",
      noCurrentEvents: "No upcoming events are open for registration right now. Please check back soon.",
      backHome: "Back to Home",
      viewEvent: "View Event",
    },
    fr: {
      title: "Inscription à la Conférence",
      subtitle: "Complétez toutes les sections pour vous inscrire à la Conférence Internationale Ottawa 2027",
      conferenceTitle: "Conférence Internationale Ottawa 2027",
      conferenceSubject: "COMMENT SE RELEVER APRES: ABUS, VIOL, HARCÈLEMENT, MALTRAITANCE ET TRAUMATISME",
      date: "12-14 Mars 2027",
      location: "Hôtel Sheraton, 150 Albert St, Ottawa, Canada",
      fee: "Frais d'inscription : 499 $ USD",
      feeNote: "Le paiement sera demandé après l'approbation de votre inscription par les organisateurs.",

      step1: "Informations Personnelles",
      step1desc: "Telles qu'elles apparaissent sur votre passeport / pièce d'identité",
      step2: "Adresse et Profession",
      step2desc: "Votre adresse de résidence et vos détails professionnels",
      step3: "Voyage et Visa",
      step3desc: "Dispositions de voyage et demande de lettre d'invitation pour visa",
      step4: "Details de la Conference",
      step4desc: "Votre motivation et vos besoins spéciaux",
      step5: "Contact d'Urgence",
      step5desc: "En cas d'urgence pendant la conférence",

      dateOfBirth: "Date de Naissance",
      gender: "Genre",
      genderOptions: { male: "Homme", female: "Femme", other: "Autre", prefer_not: "Préfère ne pas dire" },
      nationality: "Nationalité",
      passportNumber: "Numéro de Passeport",
      passportExpiry: "Date d'Expiration du Passeport",
      placeOfBirth: "Lieu de Naissance (Ville, Pays)",
      passportNote: "Requis pour la lettre d'invitation visa. Vos données sont strictement confidentielles.",

      address: "Adresse",
      city: "Ville",
      stateProvince: "État / Province",
      postalCode: "Code Postal",
      country: "Pays de Résidence",
      occupation: "Titre du Poste / Profession",
      organization: "Organisation / Entreprise",
      organizationAddress: "Adresse de l'Organisation",

      arrivalDate: "Date d'Arrivée Prévue",
      departureDate: "Date de Départ Prévue",
      needsVisaLetter: "J'ai besoin d'une lettre d'invitation visa pour mon voyage au Canada",
      visaEmbassyCity: "Ville de l'Ambassade/Consulat du Canada",
      visaCountry: "Pays de la Demande de Visa",
      visaNote: "Si vous avez besoin d'une lettre d'invitation visa, veuillez fournir vos détails de passeport à l'étape 1 et les informations de l'ambassade ci-dessous. La lettre vous sera envoyée après approbation.",

      reason: "Pourquoi souhaitez-vous participer à cette conférence?",
      reasonPlaceholder: "Partagez votre motivation, ce que vous espérez gagner, et comment cette conference est liée à votre travail ou parcours personnel...",
      dietaryRequirements: "Besoins Alimentaires",
      dietaryPlaceholder: "ex. Végétarien, Halal, Sans gluten, Allergies...",
      heardAbout: "Comment avez-vous entendu parler de nous?",
      heardOptions: { social: "Réseaux Sociaux", friend: "Ami / Collègue", church: "Église / Communauté", website: "Site Web", email: "Courriel", other: "Autre" },
      specialNeeds: "Besoins Spéciaux ou Aménagements",
      specialPlaceholder: "Acces en fauteuil roulant, assistance auditive, garde d'enfants, etc.",

      emergencyContact: "Nom Complet",
      emergencyPhone: "Telephone (avec indicatif du pays)",
      emergencyRelationship: "Lien de Parenté",
      relationshipOptions: { spouse: "Conjoint(e)", parent: "Parent", sibling: "Frère/Sœur", friend: "Ami(e)", colleague: "Collègue", other: "Autre" },

      terms: "Je certifie que les informations fournies sont exactes. J'accepte les termes et conditions de la conference et comprends que mon inscription sera examinée avant approbation.",
      submit: "Soumettre l'Inscription",
      next: "Continuer",
      previous: "Retour",
      backDashboard: "Retour au Tableau de Bord",
      success: "Inscription soumise avec succès! Votre demande est en attente de révision.",
      loginRequired: "Veuillez vous connecter pour vous inscrire à la conférence",
      login: "Se connecter",
      loading: "Soumission...",
      required: "Requis",
      optional: "Optionnel",
      stepOf: "de",
      eventClosedTitle: "Inscriptions Fermées — Événement Terminé",
      eventClosedBody: "Cette conférence est déjà terminée. Merci de votre intérêt ! Vous ne pouvez pas vous inscrire à un événement passé.",
      currentEventsTitle: "Événements Actuels et à Venir",
      noCurrentEvents: "Aucun événement à venir n'est ouvert aux inscriptions pour le moment. Revenez bientôt.",
      backHome: "Retour à l'Accueil",
      viewEvent: "Voir l'Événement",
    }
  }[language] || {
    // fallback to fr
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
    setError('');
  };

  // Load existing registration in edit mode
  useEffect(() => {
    // Fetch events to check if current event is still open
    axios.get(`${BACKEND_URL}/api/events`).then(res => setEventsData(res.data)).catch(() => {});

    if (isEditMode && user && token) {
      axios.get(`${BACKEND_URL}/api/registrations/my-registration`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        const r = res.data;
        setFormData(prev => ({
          ...prev,
          date_of_birth: r.date_of_birth || '',
          gender: r.gender || '',
          nationality: r.nationality || '',
          passport_number: r.passport_number || '',
          passport_expiry: r.passport_expiry || '',
          place_of_birth: r.place_of_birth || '',
          address: r.address || '',
          city: r.city || '',
          state_province: r.state_province || '',
          postal_code: r.postal_code || '',
          country: r.country || '',
          occupation: r.occupation || '',
          organization: r.organization || '',
          organization_address: r.organization_address || '',
          arrival_date: r.arrival_date || '',
          departure_date: r.departure_date || '',
          needs_visa_letter: r.needs_visa_letter || false,
          visa_embassy_city: r.visa_embassy_city || '',
          visa_country: r.visa_country || '',
          reason: r.reason || '',
          dietary_requirements: r.dietary_requirements || '',
          heard_about: r.heard_about || '',
          special_needs: r.special_needs || '',
          emergency_contact: r.emergency_contact || '',
          emergency_phone: r.emergency_phone || '',
          emergency_relationship: r.emergency_relationship || '',
        }));
        if (r.admin_note) setAdminNote(r.admin_note);
      }).catch(() => {});
    }
  }, [isEditMode, user, token]);

  const nextStep = () => { if (step < totalSteps) setStep(step + 1); window.scrollTo(0, 0); };
  const prevStep = () => { if (step > 1) setStep(step - 1); window.scrollTo(0, 0); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) { navigate('/login'); return; }
    setLoading(true);
    const payload = {
      date_of_birth: formData.date_of_birth,
      gender: formData.gender,
      nationality: formData.nationality,
      passport_number: formData.passport_number || null,
      passport_expiry: formData.passport_expiry || null,
      place_of_birth: formData.place_of_birth || null,
      address: formData.address,
      city: formData.city,
      state_province: formData.state_province || null,
      postal_code: formData.postal_code || null,
      country: formData.country,
      occupation: formData.occupation,
      organization: formData.organization || null,
      organization_address: formData.organization_address || null,
      arrival_date: formData.arrival_date || null,
      departure_date: formData.departure_date || null,
      needs_visa_letter: formData.needs_visa_letter,
      visa_embassy_city: formData.visa_embassy_city || null,
      visa_country: formData.visa_country || null,
      reason: formData.reason,
      dietary_requirements: formData.dietary_requirements || null,
      heard_about: formData.heard_about || null,
      special_needs: formData.special_needs || null,
      emergency_contact: formData.emergency_contact,
      emergency_phone: formData.emergency_phone,
      emergency_relationship: formData.emergency_relationship || null,
    };
    try {
      if (isEditMode) {
        await axios.put(`${BACKEND_URL}/api/registrations/update`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${BACKEND_URL}/api/registrations`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      navigate('/dashboard', { state: { message: t.success } });
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred');
    } finally { setLoading(false); }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#fafafa] flex flex-col">
        <div className="bg-[#1D1D1D] py-4 px-4">
          <div className="max-w-7xl mx-auto">
            <Link to="/">
              <img src="https://customer-assets.emergentagent.com/job_talitha-koumi/artifacts/xsd39lyf_IMG-20260121-WA0012.jpg" alt="TALITHA KOUMI TUG" className="h-10" />
            </Link>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-gray-600 mb-4">{t.loginRequired}</p>
            <Link to="/login" className="bg-[#CD0000] text-white px-6 py-3 rounded-xl font-bold">{t.login}</Link>
          </div>
        </div>
      </div>
    );
  }

  const inputClass = "w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent bg-white transition-all";
  const labelClass = "block text-sm font-semibold text-[#1D1D1D] mb-1.5";
  const optionalBadge = <span className="text-gray-400 font-normal text-xs ml-1">({t.optional})</span>;

  const stepLabels = [
    { num: 1, icon: User, label: t.step1 },
    { num: 2, icon: Building, label: t.step2 },
    { num: 3, icon: Plane, label: t.step3 },
    { num: 4, icon: FileText, label: t.step4 },
    { num: 5, icon: Shield, label: t.step5 },
  ];

  // Block registration when conference has ended
  // Only blocks for new registrations (isEditMode users can still view/update their existing submission)
  if (!isEditMode && eventsData && !eventsData.current) {
    const pastEvents = (eventsData.events || []).filter(ev => ev.is_past);
    const upcoming = (eventsData.events || []).filter(ev => !ev.is_past);
    return (
      <div data-testid="event-closed-page" className="min-h-screen bg-[#fafafa]">
        <div className="bg-[#1D1D1D] py-3 px-4">
          <div className="max-w-5xl mx-auto flex justify-between items-center">
            <Link to="/" className="flex items-center gap-3">
              <img src="https://customer-assets.emergentagent.com/job_talitha-koumi/artifacts/xsd39lyf_IMG-20260121-WA0012.jpg" alt="TALITHA KOUMI TUG" className="h-10" />
            </Link>
            <Link to="/" className="text-white/60 hover:text-white flex items-center gap-1 text-sm transition-colors">
              <ArrowLeft className="w-4 h-4" /> {t.backHome}
            </Link>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-4 py-12">
          {/* Closed banner */}
          <div className="bg-gradient-to-br from-red-50 via-orange-50 to-amber-50 border border-red-200 rounded-3xl p-8 mb-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-extrabold text-[#1D1D1D] mb-2">{t.eventClosedTitle}</h1>
            <p className="text-gray-600 leading-relaxed">{t.eventClosedBody}</p>
            {pastEvents.length > 0 && (
              <p className="text-gray-500 text-sm mt-4 italic">
                {language === 'fr' ? 'Dernier événement : ' : 'Last event: '}
                <strong>{language === 'fr' ? pastEvents[0].title_fr : pastEvents[0].title}</strong>
                {' — '}{pastEvents[0].end_date}
              </p>
            )}
          </div>

          {/* Current events */}
          <h2 className="text-xl font-extrabold text-[#1D1D1D] mb-4">{t.currentEventsTitle}</h2>
          {upcoming.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
              <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">{t.noCurrentEvents}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {upcoming.map((ev) => (
                <div key={ev.id} data-testid={`current-event-${ev.id}`} className="bg-white rounded-2xl border border-gray-100 p-5 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#CD0000]/10 flex items-center justify-center shrink-0">
                    <Calendar className="w-6 h-6 text-[#CD0000]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-[#1D1D1D] mb-1">{language === 'fr' ? ev.title_fr : ev.title}</h3>
                    <p className="text-xs text-gray-500 mb-2">{ev.start_date} → {ev.end_date} · {ev.venue}</p>
                    <p className="text-sm text-gray-600 italic">« {language === 'fr' ? ev.theme_fr : ev.theme_en} »</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fafafa]">
      {/* Header */}
      <div className="bg-[#1D1D1D] py-3 px-4">
        <div className="max-w-5xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_talitha-koumi/artifacts/xsd39lyf_IMG-20260121-WA0012.jpg" alt="TALITHA KOUMI TUG" className="h-10" />
            <span className="text-white/60 text-xs font-bold tracking-widest hidden sm:inline">CONFERENCE 2027</span>
          </Link>
          <Link to="/dashboard" data-testid="back-to-dashboard" className="text-white/60 hover:text-white flex items-center gap-1 text-sm transition-colors">
            <ArrowLeft className="w-4 h-4" /> {t.backDashboard}
          </Link>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Conference Banner */}
        <div className="relative rounded-2xl overflow-hidden h-40 mb-8">
          <img src="https://customer-assets.emergentagent.com/job_talitha-koumi/artifacts/n2geeql4_file_00000000e4dc722f93e14f7e2b511ce8.png" alt="Conference" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
          <div className="absolute inset-0 p-6 flex flex-col justify-center">
            <p className="text-[#CD0000] font-bold text-xs tracking-widest mb-1">{t.date}</p>
            <h2 className="text-white text-lg md:text-xl font-extrabold leading-tight mb-1">{t.conferenceTitle}</h2>
            <div className="flex flex-wrap items-center gap-4 text-white/70 text-xs">
              <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{t.location}</span>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="mb-6">
          <h1 className="text-2xl font-extrabold text-[#1D1D1D]">{t.title}</h1>
          <p className="text-gray-500 text-sm mt-1">{t.subtitle}</p>
        </div>

        {/* Step Indicator */}
        <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-6">
          <div className="flex items-center justify-between">
            {stepLabels.map((s, i) => (
              <React.Fragment key={s.num}>
                <button
                  onClick={() => setStep(s.num)}
                  data-testid={`step-indicator-${s.num}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold transition-all ${
                    step === s.num ? 'bg-[#1D1D1D] text-white' :
                    step > s.num ? 'bg-[#D4AF37]/10 text-[#D4AF37]' :
                    'bg-gray-50 text-gray-400'
                  }`}
                >
                  {step > s.num ? <Check className="w-3.5 h-3.5" /> : <s.icon className="w-3.5 h-3.5" />}
                  <span className="hidden md:inline">{s.label}</span>
                  <span className="md:hidden">{s.num}</span>
                </button>
                {i < stepLabels.length - 1 && <div className={`flex-1 h-0.5 mx-2 rounded ${step > s.num ? 'bg-[#D4AF37]' : 'bg-gray-100'}`} />}
              </React.Fragment>
            ))}
          </div>
          <p className="text-center text-gray-400 text-xs mt-3">
            {t[`step${step}`]} — {step} {t.stepOf} {totalSteps}
          </p>
        </div>

        {error && (
          <div data-testid="conference-error-message" className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl mb-6 text-sm flex items-center gap-2">
            <AlertCircle className="w-5 h-5 shrink-0" /> {error}
          </div>
        )}

        {isEditMode && adminNote && (
          <div data-testid="admin-note-banner" className="bg-orange-50 border-2 border-orange-300 rounded-xl p-5 mb-6 flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-orange-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-extrabold text-orange-800 text-sm mb-1">{language === 'fr' ? 'Note de l\'Admin — Veuillez corriger:' : 'Admin Note — Please correct:'}</p>
              <p className="text-orange-700 text-sm">{adminNote}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} data-testid="conference-registration-form">
          {/* ===== STEP 1: Personal ===== */}
          {step === 1 && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8 space-y-6">
              <div>
                <h3 className="text-lg font-extrabold text-[#1D1D1D] flex items-center gap-2">
                  <User className="w-5 h-5 text-[#D4AF37]" /> {t.step1}
                </h3>
                <p className="text-gray-500 text-xs mt-1">{t.step1desc}</p>
              </div>

              <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-xs text-blue-700 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{t.passportNote}</span>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>{t.dateOfBirth} <span className="text-[#CD0000]">*</span></label>
                  <input data-testid="conf-dob-input" type="date" name="date_of_birth" value={formData.date_of_birth} onChange={handleChange} required className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>{t.gender} <span className="text-[#CD0000]">*</span></label>
                  <select data-testid="conf-gender-select" name="gender" value={formData.gender} onChange={handleChange} required className={inputClass}>
                    <option value="">---</option>
                    {Object.entries(t.genderOptions).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>{t.nationality} <span className="text-[#CD0000]">*</span></label>
                  <input data-testid="conf-nationality-input" type="text" name="nationality" value={formData.nationality} onChange={handleChange} required className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>{t.placeOfBirth} {optionalBadge}</label>
                  <input data-testid="conf-pob-input" type="text" name="place_of_birth" value={formData.place_of_birth} onChange={handleChange} placeholder="e.g. Kinshasa, DRC" className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>{t.passportNumber} {optionalBadge}</label>
                  <input data-testid="conf-passport-input" type="text" name="passport_number" value={formData.passport_number} onChange={handleChange} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>{t.passportExpiry} {optionalBadge}</label>
                  <input data-testid="conf-passport-expiry-input" type="date" name="passport_expiry" value={formData.passport_expiry} onChange={handleChange} className={inputClass} />
                </div>
              </div>
            </div>
          )}

          {/* ===== STEP 2: Address & Professional ===== */}
          {step === 2 && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8 space-y-6">
              <div>
                <h3 className="text-lg font-extrabold text-[#1D1D1D] flex items-center gap-2">
                  <Building className="w-5 h-5 text-[#D4AF37]" /> {t.step2}
                </h3>
                <p className="text-gray-500 text-xs mt-1">{t.step2desc}</p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className={labelClass}>{t.address} <span className="text-[#CD0000]">*</span></label>
                  <input data-testid="conf-address-input" type="text" name="address" value={formData.address} onChange={handleChange} required className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>{t.city} <span className="text-[#CD0000]">*</span></label>
                  <input data-testid="conf-city-input" type="text" name="city" value={formData.city} onChange={handleChange} required className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>{t.stateProvince} {optionalBadge}</label>
                  <input data-testid="conf-state-input" type="text" name="state_province" value={formData.state_province} onChange={handleChange} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>{t.postalCode} {optionalBadge}</label>
                  <input data-testid="conf-postal-input" type="text" name="postal_code" value={formData.postal_code} onChange={handleChange} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>{t.country} <span className="text-[#CD0000]">*</span></label>
                  <input data-testid="conf-country-input" type="text" name="country" value={formData.country} onChange={handleChange} required className={inputClass} />
                </div>
              </div>

              <hr className="border-gray-100" />

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>{t.occupation} <span className="text-[#CD0000]">*</span></label>
                  <input data-testid="conf-occupation-input" type="text" name="occupation" value={formData.occupation} onChange={handleChange} required className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>{t.organization} {optionalBadge}</label>
                  <input data-testid="conf-organization-input" type="text" name="organization" value={formData.organization} onChange={handleChange} className={inputClass} />
                </div>
                <div className="md:col-span-2">
                  <label className={labelClass}>{t.organizationAddress} {optionalBadge}</label>
                  <input data-testid="conf-org-address-input" type="text" name="organization_address" value={formData.organization_address} onChange={handleChange} className={inputClass} />
                </div>
              </div>
            </div>
          )}

          {/* ===== STEP 3: Travel & Visa ===== */}
          {step === 3 && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8 space-y-6">
              <div>
                <h3 className="text-lg font-extrabold text-[#1D1D1D] flex items-center gap-2">
                  <Plane className="w-5 h-5 text-[#D4AF37]" /> {t.step3}
                </h3>
                <p className="text-gray-500 text-xs mt-1">{t.step3desc}</p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>{t.arrivalDate} {optionalBadge}</label>
                  <input data-testid="conf-arrival-input" type="date" name="arrival_date" value={formData.arrival_date} onChange={handleChange} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>{t.departureDate} {optionalBadge}</label>
                  <input data-testid="conf-departure-input" type="date" name="departure_date" value={formData.departure_date} onChange={handleChange} className={inputClass} />
                </div>
              </div>

              <div className={`rounded-xl p-4 border-2 transition-all ${formData.needs_visa_letter ? 'bg-[#CD0000]/5 border-[#CD0000]/30' : 'bg-gray-50 border-gray-100'}`}>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    data-testid="conf-visa-checkbox"
                    type="checkbox"
                    name="needs_visa_letter"
                    checked={formData.needs_visa_letter}
                    onChange={handleChange}
                    className="mt-0.5 w-5 h-5 text-[#CD0000] border-gray-300 rounded focus:ring-[#CD0000]"
                  />
                  <div>
                    <span className="font-bold text-sm text-[#1D1D1D]">{t.needsVisaLetter}</span>
                    <p className="text-gray-500 text-xs mt-1">{t.visaNote}</p>
                  </div>
                </label>
              </div>

              {formData.needs_visa_letter && (
                <div className="grid md:grid-cols-2 gap-4 pl-2 border-l-4 border-[#CD0000]/30">
                  <div>
                    <label className={labelClass}>{t.visaEmbassyCity} <span className="text-[#CD0000]">*</span></label>
                    <input data-testid="conf-embassy-input" type="text" name="visa_embassy_city" value={formData.visa_embassy_city} onChange={handleChange} required={formData.needs_visa_letter} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>{t.visaCountry} <span className="text-[#CD0000]">*</span></label>
                    <input data-testid="conf-visa-country-input" type="text" name="visa_country" value={formData.visa_country} onChange={handleChange} required={formData.needs_visa_letter} className={inputClass} />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ===== STEP 4: Conference Details ===== */}
          {step === 4 && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8 space-y-6">
              <div>
                <h3 className="text-lg font-extrabold text-[#1D1D1D] flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#D4AF37]" /> {t.step4}
                </h3>
                <p className="text-gray-500 text-xs mt-1">{t.step4desc}</p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className={labelClass}>{t.reason} <span className="text-[#CD0000]">*</span></label>
                  <textarea data-testid="conf-reason-input" name="reason" value={formData.reason} onChange={handleChange} required rows={4} placeholder={t.reasonPlaceholder} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>{t.heardAbout} {optionalBadge}</label>
                  <select data-testid="conf-heard-select" name="heard_about" value={formData.heard_about} onChange={handleChange} className={inputClass}>
                    <option value="">---</option>
                    {Object.entries(t.heardOptions).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>{t.dietaryRequirements} {optionalBadge}</label>
                  <input data-testid="conf-dietary-input" type="text" name="dietary_requirements" value={formData.dietary_requirements} onChange={handleChange} placeholder={t.dietaryPlaceholder} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>{t.specialNeeds} {optionalBadge}</label>
                  <textarea data-testid="conf-special-input" name="special_needs" value={formData.special_needs} onChange={handleChange} rows={2} placeholder={t.specialPlaceholder} className={inputClass} />
                </div>
              </div>
            </div>
          )}

          {/* ===== STEP 5: Emergency Contact ===== */}
          {step === 5 && (
            <div className="space-y-6">
              <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8 space-y-6">
                <div>
                  <h3 className="text-lg font-extrabold text-[#1D1D1D] flex items-center gap-2">
                    <Shield className="w-5 h-5 text-[#D4AF37]" /> {t.step5}
                  </h3>
                  <p className="text-gray-500 text-xs mt-1">{t.step5desc}</p>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>{t.emergencyContact} <span className="text-[#CD0000]">*</span></label>
                    <input data-testid="conf-emergency-name-input" type="text" name="emergency_contact" value={formData.emergency_contact} onChange={handleChange} required className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>{t.emergencyPhone} <span className="text-[#CD0000]">*</span></label>
                    <input data-testid="conf-emergency-phone-input" type="tel" name="emergency_phone" value={formData.emergency_phone} onChange={handleChange} required placeholder="+1 613-000-0000" className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>{t.emergencyRelationship} {optionalBadge}</label>
                    <select data-testid="conf-emergency-rel-select" name="emergency_relationship" value={formData.emergency_relationship} onChange={handleChange} className={inputClass}>
                      <option value="">---</option>
                      {Object.entries(t.relationshipOptions).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Terms */}
              <div className={`rounded-xl p-4 border-2 transition-all ${formData.agreeTerms ? 'bg-[#D4AF37]/5 border-[#D4AF37]/30' : 'bg-gray-50 border-gray-100'}`}>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="agreeTerms"
                    data-testid="conf-agree-terms"
                    checked={formData.agreeTerms}
                    onChange={handleChange}
                    required
                    className="mt-0.5 w-5 h-5 text-[#D4AF37] border-gray-300 rounded focus:ring-[#D4AF37]"
                  />
                  <span className="text-sm text-gray-700">{t.terms}</span>
                </label>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center mt-8">
            {step > 1 ? (
              <button type="button" onClick={prevStep} data-testid="conf-prev-btn" className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-bold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors">
                <ArrowLeft className="w-4 h-4" /> {t.previous}
              </button>
            ) : <div />}

            {step < totalSteps ? (
              <button type="button" onClick={nextStep} data-testid="conf-next-btn" className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold bg-[#1D1D1D] text-white hover:bg-[#333] transition-colors">
                {t.next} <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="submit"
                data-testid="conf-submit-btn"
                disabled={loading || !formData.agreeTerms}
                className="flex items-center gap-2 px-8 py-3 rounded-xl text-sm font-bold bg-[#CD0000] text-white hover:bg-[#c91560] transition-colors disabled:opacity-50"
              >
                {loading ? t.loading : t.submit} <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConferenceRegistration;
